import { useState } from "react";
import type { JobRecord } from "@/types/domain";
import { Button } from "./Button";

interface Props {
  job: JobRecord;
  onSubmit: (values: { note: string; amount: number | null }) => Promise<void>;
  onCancel: () => void;
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "10px 12px",
  border: "1px solid var(--line-2)",
  borderRadius: 8,
  fontSize: 14,
  background: "var(--bg)",
  boxSizing: "border-box",
};

export function JobLogModal({ job, onSubmit, onCancel }: Props) {
  const requiresComment = job.data.require_comment;
  const requiresPrice = job.data.price === null;

  const [note, setNote] = useState("");
  const [priceInput, setPriceInput] = useState("");
  const [noteError, setNoteError] = useState<string | null>(null);
  const [priceError, setPriceError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    let valid = true;

    if (requiresComment && note.trim() === "") {
      setNoteError("Skriv en kommentar.");
      valid = false;
    } else {
      setNoteError(null);
    }

    let amount: number | null = null;
    if (requiresPrice) {
      const trimmed = priceInput.trim();
      const parsed = Number(trimmed);
      if (trimmed === "" || !Number.isFinite(parsed)) {
        setPriceError("Skriv inn en pris.");
        valid = false;
      } else if (parsed <= 0) {
        setPriceError("Prisen må være større enn 0.");
        valid = false;
      } else {
        setPriceError(null);
        amount = parsed;
      }
    }

    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit({ note: note.trim(), amount });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(31, 42, 36, 0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onCancel}
    >
      <div
        data-testid="job-log-modal"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--surface)",
          borderRadius: "var(--r-xl)",
          boxShadow: "var(--shadow-md)",
          padding: 24,
          width: 360,
          maxWidth: "calc(100vw - 32px)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 18,
          }}
        >
          <span style={{ fontSize: 28 }}>{job.data.emoji}</span>
          <div
            style={{
              fontFamily: "var(--font-display)",
              fontStyle: "italic",
              fontSize: 22,
              fontWeight: 400,
              letterSpacing: -0.5,
            }}
          >
            {job.data.title}
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {requiresComment && (
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "var(--ink)",
                  marginBottom: 8,
                }}
              >
                {job.data.comment_label}
              </div>
              <textarea
                data-testid="job-log-comment-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                style={{ ...inputStyle, resize: "vertical", minHeight: 72 }}
              />
              {noteError && (
                <div
                  data-testid="job-log-comment-error"
                  style={{ color: "var(--bad)", fontSize: 13, marginTop: 6 }}
                >
                  {noteError}
                </div>
              )}
            </div>
          )}

          {requiresPrice && (
            <div>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: 0.6,
                  color: "var(--ink)",
                  marginBottom: 8,
                }}
              >
                Foreslå pris (kr)
              </div>
              <input
                data-testid="job-log-price-input"
                type="number"
                inputMode="numeric"
                min={1}
                value={priceInput}
                onChange={(e) => setPriceInput(e.target.value)}
                style={inputStyle}
              />
              {priceError && (
                <div
                  data-testid="job-log-price-error"
                  style={{ color: "var(--bad)", fontSize: 13, marginTop: 6 }}
                >
                  {priceError}
                </div>
              )}
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
            marginTop: 24,
          }}
        >
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            data-testid="job-log-cancel-button"
          >
            Avbryt
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="job-log-submit-button"
          >
            {submitting ? "Lagrer…" : "Logg"}
          </Button>
        </div>
      </div>
    </div>
  );
}
